// Hand-maintained — override generation
import {
  getAdminAgents,
  getAdminAgentsById,
  getAdminAgentsBySlugBySlug,
  postAdminAgentsByIdClone,
  postAdminAgentsCloneForWorkspace,
  postAdminAgentsByIdExport,
  postAdminAgentsImport,
  postAdminAgentsByIdTest,
  postAdminAgentsByIdValidate,
  postAdminAgentsByIdTeach,
  postAdminAgentsByIdPublishVersion,
  postAdminAgentsByIdRestoreVersion,
  postAdminAgentsByIdAnalyzeTraining,
  getAdminAgentsByIdStats,
  getAdminAgentsByIdUsage,
  getAdminAgentsUsage,
  getAdminAgentsByIdTrainingStats,
  getAdminAgentVersions,
  getAdminAgentVersionsById,
  deleteAdminAgentVersionsById,
  postAdminAgentVersions,
  postAdminAgentVersionsByIdAddSystemField,
  postAdminAgentVersionsByIdRemoveSystemField,
  postAdminAgentVersionsByIdSetSystemFields,
  getAdminAgentVersionsByIdMetrics,
  getAdminAgentVersionsByIdRevisions,
  getAdminAgentVersionRevisionsById,
  getAdminAgentVersionRevisions,
  getAdminAgentsByIdSchemaVersions,
  postAdminAgentsByIdSchemaVersions,
  postAdminAgentsByIdSchemaVersionsByVersionIdActivate,
  patchAdminAgentsByIdSchemaVersionsByVersionId,
  postAdminAgentVersionComparisons,
  getAdminTrainingExamples,
  getAdminTrainingExamplesById,
  postAdminTrainingExamples,
  patchAdminTrainingExamplesById,
  deleteAdminTrainingExamplesById,
  postAdminTrainingExamplesBulk,
  postAdminTrainingExamplesBulkDelete,
  postAdminTrainingExamplesSearch,
  getAdminAgentsByIdTrainingExamples,
  deleteAdminAgentsByIdTrainingExamplesByExampleId,
  getAdminTrainingSessionsAgentsByAgentIdSessions,
  getAdminTrainingSessionsById,
  deleteAdminTrainingSessionsById,
  getAdminFieldTemplates,
  getAdminFieldTemplatesById,
  postAdminFieldTemplates,
  deleteAdminFieldTemplatesById,
  getAdminAgentDeployments,
  getAdminAgentDeploymentsById,
  postAdminAgentDeployments,
  patchAdminAgentDeploymentsById,
  patchAdminAgentDeploymentsByIdAcceptUpdate,
  deleteAdminAgentDeploymentsById,
  getAdminWorkspaceAgentConfigs,
  getAdminWorkspaceAgentConfigsById,
  postAdminWorkspaceAgentConfigs,
  patchAdminWorkspaceAgentConfigsById,
  deleteAdminWorkspaceAgentConfigsById,
  postAdminAgents,
  patchAdminAgentsById,
  deleteAdminAgentsById,
  getAdminAgentSouls,
  getAdminAgentSoulsById,
  postAdminAgentSouls,
  patchAdminAgentSoulsById,
  deleteAdminAgentSoulsById,
  getAdminAgentTools,
} from "../_internal/sdk.gen";
import type {
  Agent,
  AgentStats,
  AgentTestResult,
  AgentTrainingStats,
  AgentUsage,
  AgentVersion,
  AgentVersionComparison,
  AgentVersionRevision,
  FieldTemplate,
  TrainingAnalytics,
  TrainingExample,
  TrainingSession,
  AgentDeployment,
  WorkspaceAgentConfig,
  AgentSoul,
} from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

// ---------------------------------------------------------------------------
// Parameter types for agent methods
// ---------------------------------------------------------------------------

/** Attributes for creating an agent. */
export type CreateAgentAttributes = {
  name: string;
  description?: string;
  workspace_id?: string;
  application_id?: string;
  tenant_id?: string;
  vertical?: string;
  tags?: string[];
  instructions?: string;
  default_instructions?: string;
  execution_mode?: string;
  allow_tenant_clone?: boolean;
  allow_instruction_override?: boolean;
  output_schema_locked?: boolean;
  input_schema_locked?: boolean;
  prompt_variants?: Record<string, unknown>;
  [key: string]: unknown;
};

/** Attributes for updating an agent. */
export type UpdateAgentAttributes = {
  name?: string;
  description?: string;
  vertical?: string;
  tags?: string[];
  instructions?: string;
  default_instructions?: string;
  execution_mode?: string;
  allow_tenant_clone?: boolean;
  allow_instruction_override?: boolean;
  output_schema_locked?: boolean;
  input_schema_locked?: boolean;
  prompt_variants?: Record<string, unknown>;
  [key: string]: unknown;
};

/** Attributes for cloning an agent. */
export type CloneAgentAttributes = {
  new_name: string;
};

/** Attributes for running an agent test. */
export type TestAgentAttributes = {
  sample_input: string;
};

/** Attributes for the teach (training) action on an agent. */
export type TeachAttributes = {
  document_id: string;
  corrections: Record<string, unknown>;
  confirmed_fields?: string[];
  correction_reasons?: Record<string, unknown>;
  training_note?: string;
  verify_remaining?: boolean;
};

/** Attributes for restoring a previously published agent version. */
export type RestoreVersionAttributes = {
  version_id: string;
};

// ---------------------------------------------------------------------------
// Parameter types for agent version methods
// ---------------------------------------------------------------------------

/** Attributes for creating a new agent version. */
export type CreateAgentVersionAttributes = {
  agent_id: string;
  prompt_template?: unknown;
  version_number?: number;
  changes_summary?: string;
  delegation_requires_approval?: boolean;
  input_schema?: Record<string, unknown>;
  input_strict?: boolean;
  is_draft?: boolean;
  max_parallel_tools?: number;
  output_schema?: Record<string, unknown>;
  output_strict?: boolean;
  parent_version_id?: string;
  soul_id?: string;
  temperature?: string;
};

/** Attributes for creating a schema version. */
export type CreateSchemaVersionAttributes = {
  prompt_template: string;
  version_number: string;
  changes_summary?: string;
  input_schema?: Record<string, unknown>;
  input_strict?: boolean;
  output_schema?: Record<string, unknown>;
  output_strict?: boolean;
};

/** Attributes for updating a schema version. */
export type UpdateSchemaVersionAttributes = {
  prompt_template?: string;
  changes_summary?: string;
  input_schema?: Record<string, unknown>;
  input_strict?: boolean;
  output_schema?: Record<string, unknown>;
  output_strict?: boolean;
};

// ---------------------------------------------------------------------------
// Parameter types for training example methods
// ---------------------------------------------------------------------------

/** Attributes for creating a training example. */
export type CreateTrainingExampleAttributes = {
  agent_id: string;
  input_text: string;
  output_json: Record<string, unknown>;
  confidence_score?: number;
  corrected_value?: string;
  correction_metadata?: Record<string, unknown>;
  correction_reasons?: Record<string, unknown>;
  document_id?: string;
  document_type?: string;
  field_name?: string;
  input_image_ref?: string;
  is_confirmed?: boolean;
  original_value?: string;
  training_note?: string;
  training_session_id?: string;
  version_id?: string;
  workspace_id?: string;
};

/** Attributes for updating a training example. */
export type UpdateTrainingExampleAttributes = {
  input_text?: string;
  output_json?: Record<string, unknown>;
  correction_metadata?: Record<string, unknown>;
  correction_reasons?: Record<string, unknown>;
  input_image_ref?: string;
  training_note?: string;
};

// ---------------------------------------------------------------------------
// Parameter types for field template methods
// ---------------------------------------------------------------------------

/** Attributes for creating a field template. */
export type CreateFieldTemplateAttributes = {
  name: string;
  category: string;
  field_type?: string;
  extraction_hints?: Record<string, unknown>;
  is_system?: boolean;
  llm_instructions?: string;
  validation_pattern?: string;
};

/** An opaque export bundle produced by agent export and consumed by import. */
export type AgentExportBundle = Record<string, unknown>;

/** Result of a bulk delete operation. */
export type BulkDeleteResult = {
  deleted_count?: number;
  [key: string]: unknown;
};

/** Optional filter for system agent discovery. */
export interface ListSystemAgentsParams {
  /** Filter to a specific vertical, e.g. "clinical" or "medical". */
  vertical?: string;
}

/** Summary shape returned by the custom system-agent catalog endpoint. */
export interface SystemAgentSummary {
  id: string;
  name: string;
  description: string;
  vertical: string;
  tags: string[];
  system_slug: string | null;
  allow_tenant_clone: boolean;
  recommended_soul_id: string | null;
  recommended_model_class: string | null;
  attribution: {
    reviewer: string | null;
    reviewed_at: string | null;
    review_kind: string | null;
  } | null;
}

/**
 * Creates the `agents` namespace for the admin SDK.
 *
 * Provides full platform-level access to agents, versions, training,
 * schema management, and field templates across all workspaces.
 *
 * @param rb - The RequestBuilder bound to the authenticated admin client.
 * @returns An object with all agent-related methods and sub-namespaces.
 *
 * @example
 * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
 * const agents = await admin.agents.list();
 */
export function createAgentsNamespace(rb: RequestBuilder) {
  return {
    /**
     * Lists all agents (platform-wide, no workspace filter).
     *
     * @param options - Optional request options.
     * @returns Array of Agent objects.
     *
     * @example
     * const agents = await admin.agents.list();
     */
    list: async (options?: RequestOptions): Promise<Agent[]> => {
      return rb.execute<Agent[]>(getAdminAgents, {}, options);
    },

    /**
     * Lists platform system agents available for cloning.
     *
     * Calls `GET /isv/catalog/system-agents`, a custom Phoenix controller
     * endpoint (not AshJsonApi). `rb.rawGet` already strips the `data`
     * envelope, so this method returns the agent array directly.
     *
     * @param params - Optional filter parameters.
     * @param params.vertical - Filter to a specific vertical, e.g. `"clinical"` or `"medical"`.
     *   Omit to return all system agents across all verticals.
     * @param options - Optional request options (signal, idempotency key, headers).
     * @returns A promise that resolves to an array of `SystemAgentSummary` objects.
     */
    listSystemAgents: async (
      params?: ListSystemAgentsParams,
      options?: RequestOptions,
    ): Promise<SystemAgentSummary[]> => {
      const query = params?.vertical
        ? `?vertical=${encodeURIComponent(params.vertical)}`
        : "";
      return rb.rawGet<SystemAgentSummary[]>(
        `/isv/catalog/system-agents${query}`,
        options,
      );
    },

    /**
     * Fetches a single agent by ID.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns The Agent record.
     *
     * @example
     * const agent = await admin.agents.get('agt_01...');
     */
    get: async (id: string, options?: RequestOptions): Promise<Agent> => {
      return rb.execute<Agent>(getAdminAgentsById, { path: { id } }, options);
    },

    /**
     * Look up an agent by its system_slug. Use this for ISV-provisioned agents
     * where the slug is a stable identifier that survives database resets.
     *
     * @param slug - The system_slug of the agent (e.g., "chartless-session-prep-briefing").
     * @param options - Optional request options.
     * @returns The Agent record.
     *
     * @example
     * ```ts
     * const agent = await admin.agents.getBySlug("chartless-session-prep-briefing");
     * ```
     */
    getBySlug: async (
      slug: string,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        getAdminAgentsBySlugBySlug,
        { path: { slug } },
        options,
      );
    },

    /**
     * Clones an agent within the same workspace.
     *
     * @param id - The UUID of the agent to clone.
     * @param attributes - Optional attributes for the clone (name, description).
     * @param options - Optional request options.
     * @returns The cloned Agent.
     *
     * @example
     * const clone = await admin.agents.clone('agt_01...', { name: 'Clone of Bot' });
     */
    clone: async (
      id: string,
      attributes: CloneAgentAttributes,
      options?: RequestOptions,
    ): Promise<Agent> => {
      const { new_name } = attributes;
      return rb.execute<Agent>(
        postAdminAgentsByIdClone,
        { path: { id }, body: { data: { type: "agent", new_name } } },
        options,
      );
    },

    /**
     * Clones an agent into a different workspace.
     *
     * @param agentId - The UUID of the agent to clone.
     * @param targetWorkspaceId - The UUID of the destination workspace.
     * @param options - Optional request options.
     * @returns The cloned Agent in the target workspace.
     *
     * @example
     * const cloned = await admin.agents.cloneForWorkspace('agt_01...', 'ws_02...');
     */
    cloneForWorkspace: async (
      agentId: string,
      targetWorkspaceId: string,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        postAdminAgentsCloneForWorkspace,
        {
          body: {
            data: {
              type: "agent",
              attributes: {
                base_agent_id: agentId,
                workspace_id: targetWorkspaceId,
              },
            },
          },
        },
        options,
      );
    },

    /**
     * Creates a new agent.
     *
     * @param attributes - Agent attributes (name, workspace_id, etc.).
     * @param options - Optional request options.
     * @returns The created Agent.
     *
     * @example
     * const agent = await admin.agents.create({ name: 'My Agent', workspace_id: 'ws_01...' });
     */
    create: async (
      attributes: CreateAgentAttributes,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        postAdminAgents,
        { body: { data: { type: "agent", attributes } } },
        options,
      );
    },

    /**
     * Updates an agent by ID.
     *
     * @param id - The UUID of the agent.
     * @param attributes - Attributes to update.
     * @param options - Optional request options.
     * @returns The updated Agent.
     *
     * @example
     * await admin.agents.update('agt_01...', { name: 'Renamed Agent' });
     */
    update: async (
      id: string,
      attributes: UpdateAgentAttributes,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        patchAdminAgentsById,
        { path: { id }, body: { data: { type: "agent", id, attributes } } },
        options,
      );
    },

    /**
     * Permanently deletes an agent by ID.
     *
     * @param id - The UUID of the agent to delete.
     * @param options - Optional request options.
     * @returns True on successful deletion.
     *
     * @example
     * await admin.agents.delete('agt_01...');
     */
    delete: async (id: string, options?: RequestOptions): Promise<true> => {
      return rb.executeDelete(deleteAdminAgentsById, { path: { id } }, options);
    },

    /**
     * Exports an agent to a portable JSON bundle.
     *
     * @param id - The UUID of the agent to export.
     * @param options - Optional request options.
     * @returns The export bundle payload.
     *
     * @example
     * const bundle = await admin.agents.export('agt_01...');
     */
    export: async (
      id: string,
      options?: RequestOptions,
    ): Promise<AgentExportBundle> => {
      return rb.execute<AgentExportBundle>(
        postAdminAgentsByIdExport,
        { path: { id }, body: { data: {} } },
        options,
      );
    },

    /**
     * Imports an agent from a portable export bundle.
     *
     * @param data - The export bundle object.
     * @param options - Optional request options.
     * @returns The newly created Agent.
     *
     * @example
     * const imported = await admin.agents.import(exportBundle);
     */
    import: async (
      data: AgentExportBundle,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        postAdminAgentsImport,
        { body: { data: { type: "agent", attributes: { data } } } },
        options,
      );
    },

    /**
     * Runs an agent against a test input and returns the result.
     *
     * @param id - The UUID of the agent.
     * @param attributes - Test attributes (input, expected_output, etc.).
     * @param options - Optional request options.
     * @returns Test result payload.
     *
     * @example
     * const result = await admin.agents.test('agt_01...', { input: { text: '...' } });
     */
    test: async (
      id: string,
      attributes: TestAgentAttributes,
      options?: RequestOptions,
    ): Promise<AgentTestResult> => {
      return rb.execute<AgentTestResult>(
        postAdminAgentsByIdTest,
        { path: { id }, body: { data: { type: "agent", attributes } } },
        options,
      );
    },

    /**
     * Validates an agent's configuration without publishing.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns Validation result with any errors or warnings.
     *
     * @example
     * const result = await admin.agents.validate('agt_01...');
     */
    validate: async (id: string, options?: RequestOptions): Promise<Agent> => {
      return rb.execute<Agent>(
        postAdminAgentsByIdValidate,
        { path: { id }, body: { data: {} } },
        options,
      );
    },

    /**
     * Runs a training job (fine-tuning) on the agent using its examples.
     *
     * @param id - The UUID of the agent.
     * @param attributes - Training configuration attributes.
     * @param options - Optional request options.
     * @returns Training job status payload.
     *
     * @example
     * await admin.agents.teach('agt_01...', { epochs: 3 });
     */
    teach: async (
      id: string,
      attributes: TeachAttributes,
      options?: RequestOptions,
    ): Promise<Agent> => {
      return rb.execute<Agent>(
        postAdminAgentsByIdTeach,
        { path: { id }, body: { data: { type: "agent", attributes } } },
        options,
      );
    },

    /**
     * Publishes a new immutable version from the agent's current state.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns The newly created AgentVersion.
     *
     * @example
     * const version = await admin.agents.publishVersion('agt_01...');
     */
    publishVersion: async (
      id: string,
      options?: RequestOptions,
    ): Promise<AgentVersion> => {
      return rb.execute<AgentVersion>(
        postAdminAgentsByIdPublishVersion,
        { path: { id }, body: { data: {} } },
        options,
      );
    },

    /**
     * Restores a previously published version as the active version.
     *
     * @param id - The UUID of the agent.
     * @param attributes - Must include `version_id` of the version to restore.
     * @param options - Optional request options.
     * @returns The restored AgentVersion.
     *
     * @example
     * const version = await admin.agents.restoreVersion('agt_01...', { version_id: 'ver_01...' });
     */
    restoreVersion: async (
      id: string,
      attributes: RestoreVersionAttributes,
      options?: RequestOptions,
    ): Promise<AgentVersion> => {
      const { version_id } = attributes;
      return rb.execute<AgentVersion>(
        postAdminAgentsByIdRestoreVersion,
        { path: { id }, body: { data: { type: "agent", version_id } } },
        options,
      );
    },

    /**
     * Analyzes training examples and returns insights.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns Training analysis result.
     *
     * @example
     * const analysis = await admin.agents.analyzeTraining('agt_01...');
     */
    analyzeTraining: async (
      id: string,
      options?: RequestOptions,
    ): Promise<TrainingAnalytics> => {
      return rb.execute<TrainingAnalytics>(
        postAdminAgentsByIdAnalyzeTraining,
        { path: { id }, body: { data: {} } },
        options,
      );
    },

    /**
     * Retrieves usage statistics for a single agent.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns Usage data for the agent.
     *
     * @example
     * const usage = await admin.agents.usage('agt_01...');
     */
    usage: async (
      id: string,
      options?: RequestOptions,
    ): Promise<AgentUsage> => {
      return rb.execute<AgentUsage>(
        getAdminAgentsByIdUsage,
        { path: { id } },
        options,
      );
    },

    /**
     * Retrieves usage information for all agents.
     *
     * @param options - Optional request options.
     * @returns Array of usage data for all agents.
     *
     * @example
     * const allUsage = await admin.agents.usageAll();
     */
    usageAll: async (options?: RequestOptions): Promise<AgentUsage[]> => {
      return rb.execute<AgentUsage[]>(getAdminAgentsUsage, {}, options);
    },

    /**
     * Retrieves execution and performance statistics for an agent.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns Statistics payload.
     *
     * @example
     * const stats = await admin.agents.stats('agt_01...');
     */
    stats: async (
      id: string,
      options?: RequestOptions,
    ): Promise<AgentStats> => {
      return rb.execute<AgentStats>(
        getAdminAgentsByIdStats,
        { path: { id } },
        options,
      );
    },

    /**
     * Retrieves training statistics for a single agent.
     *
     * @param id - The UUID of the agent.
     * @param options - Optional request options.
     * @returns Training statistics data.
     *
     * @example
     * const stats = await admin.agents.trainingStats('agt_01...');
     */
    trainingStats: async (
      id: string,
      options?: RequestOptions,
    ): Promise<AgentTrainingStats> => {
      return rb.execute<AgentTrainingStats>(
        getAdminAgentsByIdTrainingStats,
        { path: { id } },
        options,
      );
    },

    /**
     * Sub-namespace for managing immutable agent versions.
     */
    versions: {
      /**
       * Lists all agent versions.
       *
       * @param options - Optional request options.
       * @returns Array of AgentVersion objects.
       *
       * @example
       * const versions = await admin.agents.versions.list();
       */
      list: async (options?: RequestOptions): Promise<AgentVersion[]> => {
        return rb.execute<AgentVersion[]>(getAdminAgentVersions, {}, options);
      },

      /**
       * Fetches a single agent version by ID.
       *
       * @param id - The UUID of the agent version.
       * @param options - Optional request options.
       * @returns The AgentVersion record.
       *
       * @example
       * const version = await admin.agents.versions.get('ver_01...');
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentVersion> => {
        return rb.execute<AgentVersion>(
          getAdminAgentVersionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Permanently deletes an agent version.
       *
       * @param id - The UUID of the version to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.versions.delete('ver_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminAgentVersionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new agent version snapshot.
       *
       * @param attributes - Version attributes (agent_id, prompt_template, fields, etc.).
       * @param options - Optional request options.
       * @returns The newly created AgentVersion.
       *
       * @example
       * const version = await admin.agents.versions.create({ agent_id: 'agt_01...', ... });
       */
      create: async (
        attributes: CreateAgentVersionAttributes,
        options?: RequestOptions,
      ): Promise<AgentVersion> => {
        return rb.execute<AgentVersion>(
          postAdminAgentVersions,
          { body: { data: { type: "agent-version", attributes } } },
          options,
        );
      },

      /**
       * Marks a system field as selected for this version.
       *
       * @param id - The UUID of the agent version.
       * @param fieldName - The name of the system field to add (e.g. `_filename`).
       * @param options - Optional request options.
       * @returns The updated AgentVersion.
       *
       * @example
       * await admin.agents.versions.addSystemField('ver_01...', '_filename');
       */
      addSystemField: async (
        id: string,
        fieldName: string,
        options?: RequestOptions,
      ): Promise<AgentVersion> => {
        return rb.execute<AgentVersion>(
          postAdminAgentVersionsByIdAddSystemField,
          {
            path: { id },
            body: {
              data: {
                type: "agent-version",
                system_field_name: fieldName,
              },
            },
          },
          options,
        );
      },

      /**
       * Removes a system field from the selected set for this version.
       *
       * @param id - The UUID of the agent version.
       * @param fieldName - The name of the system field to remove (e.g. `_filename`).
       * @param options - Optional request options.
       * @returns The updated AgentVersion.
       *
       * @example
       * await admin.agents.versions.removeSystemField('ver_01...', '_filename');
       */
      removeSystemField: async (
        id: string,
        fieldName: string,
        options?: RequestOptions,
      ): Promise<AgentVersion> => {
        return rb.execute<AgentVersion>(
          postAdminAgentVersionsByIdRemoveSystemField,
          {
            path: { id },
            body: {
              data: {
                type: "agent-version",
                system_field_name: fieldName,
              },
            },
          },
          options,
        );
      },

      /**
       * Sets the full list of selected system fields for this version.
       *
       * @param id - The UUID of the agent version.
       * @param fieldNames - Array of system field names to set (e.g. `['_filename', '_pages']`).
       * @param options - Optional request options.
       * @returns The updated AgentVersion.
       *
       * @example
       * await admin.agents.versions.setSystemFields('ver_01...', ['_filename', '_pages']);
       */
      setSystemFields: async (
        id: string,
        fieldNames: string[],
        options?: RequestOptions,
      ): Promise<AgentVersion> => {
        return rb.execute<AgentVersion>(
          postAdminAgentVersionsByIdSetSystemFields,
          {
            path: { id },
            body: {
              data: {
                type: "agent-version",
                system_field_names: fieldNames,
              },
            },
          },
          options,
        );
      },

      /**
       * Retrieves performance metrics for an agent version.
       *
       * @param id - The UUID of the agent version.
       * @param options - Optional request options.
       * @returns Metrics data for the version.
       *
       * @example
       * const metrics = await admin.agents.versions.metrics('ver_01...');
       */
      metrics: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentStats> => {
        return rb.execute<AgentStats>(
          getAdminAgentVersionsByIdMetrics,
          { path: { id } },
          options,
        );
      },

      /**
       * Lists revision history for an agent version.
       *
       * @param id - The UUID of the agent version.
       * @param options - Optional request options.
       * @returns Array of revision records.
       *
       * @example
       * const revisions = await admin.agents.versions.revisions('ver_01...');
       */
      revisions: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentVersionRevision[]> => {
        return rb.execute<AgentVersionRevision[]>(
          getAdminAgentVersionsByIdRevisions,
          { path: { id } },
          options,
        );
      },

      /**
       * Fetches a single revision record by ID.
       *
       * @param id - The UUID of the revision.
       * @param options - Optional request options.
       * @returns The revision record.
       *
       * @example
       * const revision = await admin.agents.versions.getRevision('rev_01...');
       */
      getRevision: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentVersionRevision> => {
        return rb.execute<AgentVersionRevision>(
          getAdminAgentVersionRevisionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Lists all revisions across all agent versions.
       *
       * @param options - Optional request options.
       * @returns Array of revision records.
       *
       * @example
       * const revisions = await admin.agents.versions.listAllRevisions();
       */
      listAllRevisions: async (
        options?: RequestOptions,
      ): Promise<AgentVersionRevision[]> => {
        return rb.execute<AgentVersionRevision[]>(
          getAdminAgentVersionRevisions,
          {},
          options,
        );
      },

      /**
       * Compares two agent versions side-by-side.
       *
       * @param versionAId - UUID of the first version.
       * @param versionBId - UUID of the second version.
       * @param options - Optional request options.
       * @returns Comparison result with schema_diff and prompt_diff.
       *
       * @example
       * const diff = await admin.agents.versions.compare('ver_A...', 'ver_B...');
       */
      compare: async (
        versionAId: string,
        versionBId: string,
        options?: RequestOptions,
      ): Promise<AgentVersionComparison> => {
        return rb.execute<AgentVersionComparison>(
          postAdminAgentVersionComparisons,
          {
            body: {
              data: {
                type: "agent-version-comparison",
                attributes: {
                  version_a_id: versionAId,
                  version_b_id: versionBId,
                },
              },
            },
          },
          options,
        );
      },

      /**
       * Sub-namespace for managing schema versions within an agent.
       */
      schemaVersions: {
        /**
         * Lists all schema versions for an agent.
         *
         * @param agentId - The UUID of the agent.
         * @param options - Optional request options.
         * @returns Array of schema version records.
         *
         * @example
         * const versions = await admin.agents.versions.schemaVersions.list('agt_01...');
         */
        list: async (
          agentId: string,
          options?: RequestOptions,
        ): Promise<AgentVersion[]> => {
          return rb.execute<AgentVersion[]>(
            getAdminAgentsByIdSchemaVersions,
            { path: { id: agentId } },
            options,
          );
        },

        /**
         * Creates a new schema version for an agent.
         *
         * @param agentId - The UUID of the agent.
         * @param attributes - Schema version attributes.
         * @param options - Optional request options.
         * @returns The newly created schema version.
         *
         * @example
         * const sv = await admin.agents.versions.schemaVersions.create('agt_01...', { output_schema: { type: 'object', properties: {} } });
         */
        create: async (
          agentId: string,
          attributes: CreateSchemaVersionAttributes,
          options?: RequestOptions,
        ): Promise<AgentVersion> => {
          const {
            prompt_template,
            version_number,
            changes_summary,
            input_schema,
            input_strict,
            output_schema,
            output_strict,
          } = attributes;
          return rb.execute<AgentVersion>(
            postAdminAgentsByIdSchemaVersions,
            {
              path: { id: agentId },
              body: {
                data: {
                  type: "agent-version",
                  prompt_template,
                  version_number,
                  changes_summary,
                  input_schema,
                  input_strict,
                  output_schema,
                  output_strict,
                },
              },
            },
            options,
          );
        },

        /**
         * Activates a schema version, making it the live version.
         *
         * @param agentId - The UUID of the agent.
         * @param versionId - The UUID of the schema version to activate.
         * @param options - Optional request options.
         * @returns The activated schema version.
         *
         * @example
         * await admin.agents.versions.schemaVersions.activate('agt_01...', 'ver_01...');
         */
        activate: async (
          agentId: string,
          versionId: string,
          options?: RequestOptions,
        ): Promise<AgentVersion> => {
          return rb.execute<AgentVersion>(
            postAdminAgentsByIdSchemaVersionsByVersionIdActivate,
            {
              path: { id: agentId, version_id: versionId },
              body: { data: {} },
            },
            options,
          );
        },

        /**
         * Updates an existing schema version.
         *
         * @param agentId - The UUID of the agent.
         * @param versionId - The UUID of the schema version to update.
         * @param attributes - Attributes to update.
         * @param options - Optional request options.
         * @returns The updated schema version.
         *
         * @example
         * await admin.agents.versions.schemaVersions.update('agt_01...', 'ver_01...', { output_schema: { type: 'object', properties: {} } });
         */
        update: async (
          agentId: string,
          versionId: string,
          attributes: UpdateSchemaVersionAttributes,
          options?: RequestOptions,
        ): Promise<AgentVersion> => {
          const {
            prompt_template,
            changes_summary,
            input_schema,
            input_strict,
            output_schema,
            output_strict,
          } = attributes;
          return rb.execute<AgentVersion>(
            patchAdminAgentsByIdSchemaVersionsByVersionId,
            {
              path: { id: agentId, version_id: versionId },
              body: {
                data: {
                  type: "agent-version",
                  prompt_template,
                  changes_summary,
                  input_schema,
                  input_strict,
                  output_schema,
                  output_strict,
                },
              },
            },
            options,
          );
        },
      },
    },

    /**
     * Sub-namespace for managing training examples attached to agents.
     */
    training: {
      /**
       * Lists all training examples.
       *
       * @param options - Optional request options.
       * @returns Array of TrainingExample objects.
       *
       * @example
       * const examples = await admin.agents.training.list();
       */
      list: async (options?: RequestOptions): Promise<TrainingExample[]> => {
        return rb.execute<TrainingExample[]>(
          getAdminTrainingExamples,
          {},
          options,
        );
      },

      /**
       * Fetches a single training example by ID.
       *
       * @param id - The UUID of the training example.
       * @param options - Optional request options.
       * @returns The TrainingExample record.
       *
       * @example
       * const example = await admin.agents.training.get('tex_01...');
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<TrainingExample> => {
        return rb.execute<TrainingExample>(
          getAdminTrainingExamplesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new training example.
       *
       * @param attributes - Training example attributes (agent_id, input_text, output_json, etc.).
       * @param options - Optional request options.
       * @returns The created TrainingExample.
       *
       * @example
       * const example = await admin.agents.training.create({ agent_id: 'agt_01...', input_text: '...', output_json: {} });
       */
      create: async (
        attributes: CreateTrainingExampleAttributes,
        options?: RequestOptions,
      ): Promise<TrainingExample> => {
        return rb.execute<TrainingExample>(
          postAdminTrainingExamples,
          { body: { data: { type: "training-example", attributes } } },
          options,
        );
      },

      /**
       * Updates a training example's attributes.
       *
       * @param id - The UUID of the training example.
       * @param attributes - Attributes to update.
       * @param options - Optional request options.
       * @returns The updated TrainingExample.
       *
       * @example
       * const updated = await admin.agents.training.update('tex_01...', { label: 'reviewed' });
       */
      update: async (
        id: string,
        attributes: UpdateTrainingExampleAttributes,
        options?: RequestOptions,
      ): Promise<TrainingExample> => {
        return rb.execute<TrainingExample>(
          patchAdminTrainingExamplesById,
          {
            path: { id },
            body: { data: { id, type: "training-example", attributes } },
          },
          options,
        );
      },

      /**
       * Deletes a training example.
       *
       * @param id - The UUID of the training example to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.training.delete('tex_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminTrainingExamplesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates multiple training examples for one agent in a single batch operation.
       *
       * @param agentId - The UUID of the agent the examples belong to.
       * @param examples - Array of training example attribute objects.
       * @param options - Optional request options.
       * @returns Array of created TrainingExample objects.
       *
       * @example
       * const created = await admin.agents.training.bulkCreate('agt_01...', [
       *   { input_text: '...', output_json: {} },
       * ]);
       */
      bulkCreate: async (
        agentId: string,
        examples: CreateTrainingExampleAttributes[],
        options?: RequestOptions,
      ): Promise<TrainingExample[]> => {
        return rb.execute<TrainingExample[]>(
          postAdminTrainingExamplesBulk,
          {
            body: { data: { agent_id: agentId, examples } },
          },
          options,
        );
      },

      /**
       * Deletes multiple training examples for one agent in a single batch operation.
       *
       * @param agentId - The UUID of the agent the examples belong to.
       * @param ids - Array of training example UUIDs to delete.
       * @param options - Optional request options.
       * @returns Deletion result payload.
       *
       * @example
       * await admin.agents.training.bulkDelete('agt_01...', ['tex_01...', 'tex_02...']);
       */
      bulkDelete: async (
        agentId: string,
        ids: string[],
        options?: RequestOptions,
      ): Promise<BulkDeleteResult> => {
        return rb.execute<BulkDeleteResult>(
          postAdminTrainingExamplesBulkDelete,
          {
            body: { data: { agent_id: agentId, ids } },
          },
          options,
        );
      },

      /**
       * Searches training examples by semantic similarity.
       *
       * The query string is embedded server-side; you do not need to compute
       * embeddings client-side.
       *
       * @param agentId - The UUID of the agent whose training examples to search.
       * @param query - The search query text.
       * @param limit - Optional maximum number of results to return (default 5).
       * @param options - Optional request options.
       * @returns Array of matching TrainingExample objects.
       *
       * @example
       * const results = await admin.agents.training.search('agt_01...', 'invoice total');
       */
      search: async (
        agentId: string,
        query: string,
        limit?: number,
        options?: RequestOptions,
      ): Promise<TrainingExample[]> => {
        const body: {
          data: { agent_id: string; query: string; limit?: number };
        } = {
          data: { agent_id: agentId, query },
        };
        if (typeof limit === "number") body.data.limit = limit;
        return rb.execute<TrainingExample[]>(
          postAdminTrainingExamplesSearch,
          { body },
          options,
        );
      },

      /**
       * Lists training examples for a specific agent.
       *
       * @param agentId - The UUID of the agent.
       * @param options - Optional request options.
       * @returns Array of training examples for the agent.
       *
       * @example
       * const examples = await admin.agents.training.listForAgent('agt_01...');
       */
      listForAgent: async (
        agentId: string,
        options?: RequestOptions,
      ): Promise<TrainingExample[]> => {
        return rb.execute<TrainingExample[]>(
          getAdminAgentsByIdTrainingExamples,
          { path: { id: agentId } },
          options,
        );
      },

      /**
       * Deletes a training example for a specific agent.
       *
       * @param agentId - The UUID of the agent.
       * @param exampleId - The UUID of the training example.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.training.deleteForAgent('agt_01...', 'tex_01...');
       */
      deleteForAgent: async (
        agentId: string,
        exampleId: string,
        options?: RequestOptions,
      ): Promise<true> => {
        return rb.executeDelete(
          deleteAdminAgentsByIdTrainingExamplesByExampleId,
          { path: { id: agentId, example_id: exampleId } },
          options,
        );
      },

      /**
       * Sub-namespace for managing training sessions.
       */
      sessions: {
        /**
         * Lists training sessions for a specific agent.
         *
         * @param agentId - The UUID of the agent.
         * @param options - Optional request options.
         * @returns Array of training session records.
         *
         * @example
         * const sessions = await admin.agents.training.sessions.list('agt_01...');
         */
        list: async (
          agentId: string,
          options?: RequestOptions,
        ): Promise<TrainingSession[]> => {
          return rb.execute<TrainingSession[]>(
            getAdminTrainingSessionsAgentsByAgentIdSessions,
            { path: { agent_id: agentId } },
            options,
          );
        },

        /**
         * Fetches a single training session by ID.
         *
         * @param id - The UUID of the training session.
         * @param options - Optional request options.
         * @returns The training session record.
         *
         * @example
         * const session = await admin.agents.training.sessions.get('ts_01...');
         */
        get: async (
          id: string,
          options?: RequestOptions,
        ): Promise<TrainingSession> => {
          return rb.execute<TrainingSession>(
            getAdminTrainingSessionsById,
            { path: { id } },
            options,
          );
        },

        /**
         * Deletes a training session.
         *
         * @param id - The UUID of the training session.
         * @param options - Optional request options.
         * @returns True on successful deletion.
         *
         * @example
         * await admin.agents.training.sessions.delete('ts_01...');
         */
        delete: async (id: string, options?: RequestOptions): Promise<true> => {
          return rb.executeDelete(
            deleteAdminTrainingSessionsById,
            { path: { id } },
            options,
          );
        },
      },
    },

    /**
     * Sub-namespace for field templates — reusable field definitions.
     */
    fieldTemplates: {
      /**
       * Lists all available field templates.
       *
       * @param options - Optional request options.
       * @returns Array of field template objects.
       *
       * @example
       * const templates = await admin.agents.fieldTemplates.list();
       */
      list: async (options?: RequestOptions): Promise<FieldTemplate[]> => {
        return rb.execute<FieldTemplate[]>(getAdminFieldTemplates, {}, options);
      },

      /**
       * Fetches a single field template by ID.
       *
       * @param id - The UUID of the field template.
       * @param options - Optional request options.
       * @returns The field template object.
       *
       * @example
       * const template = await admin.agents.fieldTemplates.get('ftpl_01...');
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<FieldTemplate> => {
        return rb.execute<FieldTemplate>(
          getAdminFieldTemplatesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new field template.
       *
       * @param attributes - Field template attributes (name, category, field_type, etc.).
       * @param options - Optional request options.
       * @returns The created field template object.
       *
       * @example
       * const template = await admin.agents.fieldTemplates.create({ name: 'Invoice Total', category: 'finance', field_type: 'currency' });
       */
      create: async (
        attributes: CreateFieldTemplateAttributes,
        options?: RequestOptions,
      ): Promise<FieldTemplate> => {
        return rb.execute<FieldTemplate>(
          postAdminFieldTemplates,
          { body: { data: { type: "field-template", attributes } } },
          options,
        );
      },

      /**
       * Permanently deletes a field template by ID.
       *
       * @param id - The UUID of the field template to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.fieldTemplates.delete('ftpl_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminFieldTemplatesById,
          { path: { id } },
          options,
        );
      },
    },

    // ── Agent Deployments ──────────────────────────────────────────

    /**
     * Manage agent deployments — bindings from catalog agents to applications.
     */
    deployments: {
      /**
       * Lists all agent deployments.
       *
       * @param options - Optional request options (pagination, filters).
       * @returns A paginated list of agent deployments.
       *
       * @example
       * const deployments = await admin.agents.deployments.list();
       */
      list: async (options?: RequestOptions): Promise<AgentDeployment[]> => {
        return rb.execute<AgentDeployment[]>(
          getAdminAgentDeployments,
          {},
          options,
        );
      },

      /**
       * Retrieves a single agent deployment by ID.
       *
       * @param id - The UUID of the deployment.
       * @param options - Optional request options.
       * @returns The agent deployment.
       *
       * @example
       * const deployment = await admin.agents.deployments.get('dep_01...');
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentDeployment> => {
        return rb.execute<AgentDeployment>(
          getAdminAgentDeploymentsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new agent deployment.
       *
       * @param attributes - Deployment attributes (catalog_agent_id, application_id, etc.).
       * @param options - Optional request options.
       * @returns The created deployment.
       *
       * @example
       * const deployment = await admin.agents.deployments.create({
       *   catalog_agent_id: 'agent_01...',
       *   effective_version_id: 'ver_01...',
       *   application_id: 'app_01...',
       *   tenant_id: 'ten_01...',
       * });
       */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<AgentDeployment> => {
        return rb.execute<AgentDeployment>(
          postAdminAgentDeployments,
          { body: { data: { type: "agent-deployment", attributes } } },
          options,
        );
      },

      /**
       * Updates an agent deployment.
       *
       * @param id - The UUID of the deployment.
       * @param attributes - Attributes to update.
       * @param options - Optional request options.
       * @returns The updated deployment.
       *
       * @example
       * await admin.agents.deployments.update('dep_01...', { auto_deploy_agents: true });
       */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<AgentDeployment> => {
        return rb.execute<AgentDeployment>(
          patchAdminAgentDeploymentsById,
          {
            path: { id },
            body: { data: { type: "agent-deployment", id, attributes } },
          },
          options,
        );
      },

      /**
       * Accepts a pending version update on a deployment.
       *
       * @param id - The UUID of the deployment with an available update.
       * @param options - Optional request options.
       * @returns The updated deployment with new effective version.
       *
       * @example
       * await admin.agents.deployments.acceptUpdate('dep_01...');
       */
      acceptUpdate: async (
        id: string,
        options?: RequestOptions,
      ): Promise<AgentDeployment> => {
        return rb.execute<AgentDeployment>(
          patchAdminAgentDeploymentsByIdAcceptUpdate,
          {
            path: { id },
            body: { data: { type: "agent-deployment", id, attributes: {} } },
          },
          options,
        );
      },

      /**
       * Permanently deletes an agent deployment.
       *
       * @param id - The UUID of the deployment to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.deployments.delete('dep_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminAgentDeploymentsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── Workspace Agent Configs ────────────────────────────────────

    /**
     * Manage workspace-level agent config overrides.
     */
    workspaceConfigs: {
      /**
       * Lists all workspace agent configs.
       *
       * @param options - Optional request options (pagination, filters).
       * @returns A paginated list of workspace agent configs.
       *
       * @example
       * const configs = await admin.agents.workspaceConfigs.list();
       */
      list: async (
        options?: RequestOptions,
      ): Promise<WorkspaceAgentConfig[]> => {
        return rb.execute<WorkspaceAgentConfig[]>(
          getAdminWorkspaceAgentConfigs,
          {},
          options,
        );
      },

      /**
       * Retrieves a single workspace agent config by ID.
       *
       * @param id - The UUID of the config.
       * @param options - Optional request options.
       * @returns The workspace agent config.
       *
       * @example
       * const config = await admin.agents.workspaceConfigs.get('wac_01...');
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<WorkspaceAgentConfig> => {
        return rb.execute<WorkspaceAgentConfig>(
          getAdminWorkspaceAgentConfigsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new workspace agent config.
       *
       * @param attributes - Config attributes (agent_deployment_id, workspace_id, overrides).
       * @param options - Optional request options.
       * @returns The created config.
       *
       * @example
       * const config = await admin.agents.workspaceConfigs.create({
       *   agent_deployment_id: 'dep_01...',
       *   workspace_id: 'ws_01...',
       *   overrides: { prompt_template: 'Custom prompt...' },
       * });
       */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<WorkspaceAgentConfig> => {
        return rb.execute<WorkspaceAgentConfig>(
          postAdminWorkspaceAgentConfigs,
          {
            body: {
              data: { type: "workspace-agent-config", attributes },
            },
          },
          options,
        );
      },

      /**
       * Updates a workspace agent config.
       *
       * @param id - The UUID of the config.
       * @param attributes - Attributes to update.
       * @param options - Optional request options.
       * @returns The updated config.
       *
       * @example
       * await admin.agents.workspaceConfigs.update('wac_01...', {
       *   overrides: { prompt_template: 'Updated prompt...' },
       * });
       */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<WorkspaceAgentConfig> => {
        return rb.execute<WorkspaceAgentConfig>(
          patchAdminWorkspaceAgentConfigsById,
          {
            path: { id },
            body: {
              data: { type: "workspace-agent-config", id, attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes a workspace agent config.
       *
       * @param id - The UUID of the config to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.workspaceConfigs.delete('wac_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminWorkspaceAgentConfigsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── Agent Souls ────────────────────────────────────────────────

    /**
     * Manage agent souls — personality templates that govern agent behavior.
     */
    souls: {
      /**
       * Lists all agent souls.
       *
       * @param options - Optional request options (pagination, filters).
       * @returns A list of agent souls.
       *
       * @example
       * const souls = await admin.agents.souls.list();
       */
      list: async (options?: RequestOptions): Promise<AgentSoul[]> => {
        return rb.execute<AgentSoul[]>(getAdminAgentSouls, {}, options);
      },

      /**
       * Retrieves a single agent soul by ID.
       *
       * @param id - The UUID of the soul.
       * @param options - Optional request options.
       * @returns The agent soul.
       *
       * @example
       * const soul = await admin.agents.souls.get('soul_01...');
       */
      get: async (id: string, options?: RequestOptions): Promise<AgentSoul> => {
        return rb.execute<AgentSoul>(
          getAdminAgentSoulsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new agent soul.
       *
       * @param attributes - Soul attributes (name, content, scope_type, etc.).
       * @param options - Optional request options.
       * @returns The created soul.
       *
       * @example
       * const soul = await admin.agents.souls.create({
       *   name: 'Professional',
       *   content: 'You are a professional, courteous assistant...',
       *   scope_type: 'workspace',
       *   workspace_id: 'ws_01...',
       * });
       */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<AgentSoul> => {
        return rb.execute<AgentSoul>(
          postAdminAgentSouls,
          { body: { data: { type: "agent-soul", attributes } } },
          options,
        );
      },

      /**
       * Updates an agent soul.
       *
       * @param id - The UUID of the soul.
       * @param attributes - Attributes to update.
       * @param options - Optional request options.
       * @returns The updated soul.
       *
       * @example
       * await admin.agents.souls.update('soul_01...', { content: 'Updated guidelines...' });
       */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<AgentSoul> => {
        return rb.execute<AgentSoul>(
          patchAdminAgentSoulsById,
          {
            path: { id },
            body: { data: { type: "agent-soul", id, attributes } },
          },
          options,
        );
      },

      /**
       * Permanently deletes an agent soul.
       *
       * @param id - The UUID of the soul to delete.
       * @param options - Optional request options.
       * @returns True on successful deletion.
       *
       * @example
       * await admin.agents.souls.delete('soul_01...');
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminAgentSoulsById,
          { path: { id } },
          options,
        );
      },
    },

    // ── Tool Catalog ───────────────────────────────────────────────

    /**
     * Browse the agent tool catalog.
     */
    toolCatalog: {
      /**
       * Lists all available agent tools.
       *
       * @param options - Optional request options (pagination, filters).
       * @returns A list of tool catalog entries.
       *
       * @example
       * const tools = await admin.agents.toolCatalog.list();
       */
      list: async (
        options?: RequestOptions,
      ): Promise<Record<string, unknown>[]> => {
        return rb.execute<Record<string, unknown>[]>(
          getAdminAgentTools,
          {},
          options,
        );
      },
    },
  };
}

/** Return type for the agents namespace — supports per-domain type imports. */
export type AgentsAPI = ReturnType<typeof createAgentsNamespace>;
